import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@components';
import { ApiClient } from '@utils/apiClient';
import { CallerMemory, CreateAgentData } from '../../types';

interface CallerMemoryTabProps {
  formData: CreateAgentData;
  agentId?: string;
}

const CallerMemoryTab: React.FC<CallerMemoryTabProps> = ({ formData, agentId }) => {
  const { t, i18n } = useTranslation();

  const [memories, setMemories] = useState<CallerMemory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CallerMemory | null>(null);
  const [toDelete, setToDelete] = useState<CallerMemory | null>(null);
  const [deleting, setDeleting] = useState(false);

  const enabled = formData.caller_memory_enabled;

  const fetchMemories = useCallback(async () => {
    if (!agentId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ApiClient.get(`/agents/agents/${agentId}/caller-memories/`);
      if (response.ok) {
        const data: CallerMemory[] = await response.json();
        setMemories(data);
      } else {
        setError(t('agents.callerMemory.loadError'));
      }
    } catch (err) {
      setError(t('agents.callerMemory.loadError'));
    } finally {
      setLoading(false);
    }
  }, [agentId, enabled, t]);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  const handleDelete = async () => {
    if (!agentId || !toDelete) return;
    setDeleting(true);
    try {
      const response = await ApiClient.delete(
        `/agents/agents/${agentId}/caller-memories/${toDelete.id}/`
      );
      if (response.ok) {
        setMemories(prev => prev.filter(m => m.id !== toDelete.id));
        setToDelete(null);
        if (selected?.id === toDelete.id) {
          setSelected(null);
        }
      } else {
        setError(t('agents.callerMemory.deleteError'));
      }
    } catch (err) {
      setError(t('agents.callerMemory.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (value: string) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString(i18n.language);
    } catch (err) {
      return value;
    }
  };

  // Feature disabled
  if (!enabled) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <span className="material-icons text-gray-400 dark:text-gray-500 text-5xl mb-3">
          psychology_alt
        </span>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
          {t('agents.callerMemory.disabledHint')}
        </p>
      </div>
    );
  }

  // Not saved yet (create mode)
  if (!agentId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <span className="material-icons text-gray-400 dark:text-gray-500 text-5xl mb-3">
          save
        </span>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
          {t('agents.callerMemory.saveFirstHint')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white">
            {t('agents.callerMemory.title')}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {t('agents.callerMemory.subtitle')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchMemories} disabled={loading}>
          <span className="material-icons text-base mr-1">refresh</span>
          {t('common.refresh')}
        </Button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="material-icons animate-spin text-primary-500">progress_activity</span>
        </div>
      ) : memories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span className="material-icons text-gray-400 dark:text-gray-500 text-5xl mb-3">
            contacts
          </span>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
            {t('agents.callerMemory.emptyState')}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('agents.callerMemory.tableCaller')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('agents.callerMemory.tableCallCount')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('agents.callerMemory.tableLastCall')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('agents.callerMemory.tableActions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900/40">
              {memories.map(memory => (
                <tr
                  key={memory.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                >
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setSelected(memory)}
                      className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {memory.caller_number}
                    </button>
                    {memory.profile_text && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1 max-w-md">
                        {memory.profile_text}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {memory.call_count}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {formatDate(memory.last_call_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setSelected(memory)}
                        className="text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                        title={t('agents.callerMemory.view')}
                      >
                        <span className="material-icons text-lg">visibility</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setToDelete(memory)}
                        className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                        title={t('common.delete')}
                      >
                        <span className="material-icons text-lg">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.caller_number}
      >
        {selected && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300">
              <span>
                {t('agents.callerMemory.tableCallCount')}: <strong>{selected.call_count}</strong>
              </span>
              <span>
                {t('agents.callerMemory.tableLastCall')}:{' '}
                <strong>{formatDate(selected.last_call_at)}</strong>
              </span>
            </div>

            {selected.profile_text && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agents.callerMemory.profileSummary')}
                </h5>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {selected.profile_text}
                </p>
              </div>
            )}

            {Object.keys(selected.profile || {}).length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.callerMemory.profileDetails')}
                </h5>
                <dl className="space-y-2">
                  {Object.entries(selected.profile).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex flex-col sm:flex-row sm:gap-3 border-b border-gray-100 dark:border-gray-800 pb-2"
                    >
                      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 sm:w-40 shrink-0">
                        {key}
                      </dt>
                      <dd className="text-sm text-gray-700 dark:text-gray-300 break-words">
                        {typeof value === 'object'
                          ? JSON.stringify(value, null, 2)
                          : String(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setToDelete(selected);
                }}
              >
                <span className="material-icons text-base mr-1">delete</span>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={toDelete !== null}
        onClose={() => !deleting && setToDelete(null)}
        title={t('agents.callerMemory.deleteTitle')}
      >
        {toDelete && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('agents.callerMemory.deleteConfirm', { number: toDelete.caller_number })}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setToDelete(null)}
                disabled={deleting}
              >
                {t('common.cancel')}
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
                {deleting ? t('agents.callerMemory.deleting') : t('common.delete')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CallerMemoryTab;
export { CallerMemoryTab };
